/**
 * Wiring the browser's own error reporting into the log buffer.
 *
 * Four sources, because each catches something the others do not:
 *
 * - `error` events: anything thrown and not caught, including a script that
 *   failed to parse. This is what a white screen looks like from the inside.
 * - `unhandledrejection`: a promise nobody awaited. Async failures land here
 *   and nowhere else.
 * - `console.error`: how React reports a render error and a hook violation,
 *   and how most library warnings arrive.
 * - `console.warn`: the deprecation notices that turn out to be the cause a
 *   week later.
 *
 * Failed API calls are NOT hooked here. They are recorded where they are
 * raised, in `shared/api/client.ts`, because that is the only place that knows
 * the path and the status - and React Query catches them, so they never reach
 * `unhandledrejection` to be observed from out here.
 */
import { recordLog } from "./logBuffer";

/**
 * Guards against installing twice.
 *
 * Vite's hot reload re-runs this module, and React StrictMode double-invokes
 * effects. Without this, one `console.error` would be recorded two or three
 * times and the log would read as a repeating failure that never happened.
 */
let installed = false;

/** Renders an unknown thrown value as something readable. */
function describe(value: unknown): string {
  if (value instanceof Error) {
    // The stack already begins with the name and message on V8, so including
    // both would print the first line twice.
    return value.stack ?? `${value.name}: ${value.message}`;
  }
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // Circular, or a Proxy that throws on read.
    return String(value);
  }
}

/** Renders the arguments of a console call as one line. */
function describeArguments(args: unknown[]): string {
  return args.map(describe).join(" ");
}

/**
 * Starts recording browser errors into the log buffer.
 *
 * Call once, from `main.tsx`, BEFORE rendering - an error thrown while the app
 * is booting is exactly the one nobody can otherwise explain, and it happens
 * before any component could have installed this.
 *
 * Both console methods are wrapped rather than replaced: the original is always
 * called, so the developer console shows precisely what it showed before.
 */
export function installDiagnostics(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    // `event.error` is absent for a resource that failed to load, where the
    // filename is the whole of the story.
    recordLog("error", event.error ? describe(event.error) : `Failed to load ${event.filename}`);
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    recordLog("error", `Unhandled rejection: ${describe(event.reason)}`);
  });

  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    recordLog("error", describeArguments(args));
    originalError(...args);
  };

  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    recordLog("warning", describeArguments(args));
    originalWarn(...args);
  };
}
